import {
  CounterConfig,
  CounterType,
  RoutineConfig,
  RoutineType,
  RoutineWorkflow,
  useFindByQuery,
  useWorkspaceAsset,
} from '@gripp/shared-logic';
import { useCallback, useEffect, useMemo } from 'react';
import { RoutineExecutionProps, RoutineModules } from '../routine';

type IssueEditRoutineModalProps = {
  isEditRoutineLoader: boolean;
  currentTitle: string;
  issue: any;
  onRoutineEdition: (props: RoutineExecutionProps) => void;
  onAlertWorkflowDetected?: () => void;
};

export const IssueEditRoutineLoader = (props: IssueEditRoutineModalProps) => {
  const {
    issue,
    isEditRoutineLoader,
    onRoutineEdition,
    onAlertWorkflowDetected,
  } = props;
  const { workspaceAsset, isLoading: isLoadingWorkspaceAsset } =
    useWorkspaceAsset(issue?.asset?.id);
  const { data: workflowData, isLoading } = useFindByQuery({
    modelName: 'workflow',
    query: FIND_WORKFLOWS,
    variables: { filter: { workspaceAsset: { eq: workspaceAsset?.id } } },
  });

  const assetRoutine = useMemo(() => issue?.assetRoutine, [issue]);

  const getRoutineWorkflows = useCallback(
    (id: string, config: RoutineConfig, state: any) => {
      if (!id || !workflowData?.items?.length) return [];

      return workflowData.items
        .filter(
          (item: RoutineWorkflow) =>
            item.trigger?.config?.workspaceRoutineId === id
        )
        .map((workflow: RoutineWorkflow) => {
          return { ...workflow, uom: config?.uom, count: state?.count };
        });
    },
    [workflowData]
  );

  const routine = assetRoutine?.routineVersion;

  const routineState = useMemo(
    () => ({
      workspaceRoutineId: assetRoutine?.routine?.id,
      workspaceRoutineVersionId: assetRoutine?.routineVersion?.id,
      state: assetRoutine?.data,
    }),
    [assetRoutine]
  );

  const workflows = useMemo(
    () =>
      getRoutineWorkflows(
        routineState?.workspaceRoutineId ?? '',
        routine?.config as RoutineConfig,
        routineState
      ),
    [routineState, routine, getRoutineWorkflows]
  );

  useEffect(() => {
    if (
      isEditRoutineLoader &&
      !isLoadingWorkspaceAsset &&
      !isLoading &&
      workspaceAsset &&
      routine &&
      assetRoutine
    ) {
      // The only submission we can't safely revise in place is a Tally
      // counter: its edit form re-derives the baseline from the current
      // running total (not the value at submission time), and editing a
      // mid-chain entry corrupts every downstream absolute.
      //
      // Everything else is safe to revise, regardless of attached alerts:
      // - Alerts are never re-triggered on edit (the revise path doesn't call
      //   triggerAlertForRoutine).
      // - Interval alerts self-heal on the next submission (GRIPP #2890) and
      //   ceiling/floor are stateless point-in-time checks, so a value edit
      //   can't corrupt counter alert state.
      // - Non-counter alerts (daysSinceCompleted, onCompletionCount, scheduled)
      //   are time/count-based, never value-based.
      // Embedded Tally counter *questions* inside a workflow stay protected by
      // their own disabled input (isIssueEditRoutine), not this gate.
      const isTallyCounter =
        routine?.type === RoutineType.Counter &&
        (routine?.config as CounterConfig)?.counterType === CounterType.Tally;

      if (isTallyCounter) {
        onAlertWorkflowDetected?.();
        return;
      }
      const module = RoutineModules[routine?.type as RoutineType];
      onRoutineEdition({
        workspaceAsset: workspaceAsset,
        routine,
        module,
        state: routineState,
        workflows,
        isIssueEditRoutine: true,
        assetRoutine,
        sourceIssue: issue,
      });
    }
  }, [
    isEditRoutineLoader,
    isLoadingWorkspaceAsset,
    isLoading,
    workspaceAsset,
    routine,
    routineState,
    workflows,
    onRoutineEdition,
    assetRoutine,
    issue,
  ]);

  return null;
};

const FIND_WORKFLOWS = `
  query FindWorkflows($filter: WorkflowFilter) {
    findWorkflows(filter: $filter) {
      count
      items {
        id
        name
        workspace {
          id
        }
        trigger {
          type
          config
        }
        action {
          type
          config
        }
        disabled
        disabledBetween {
          start
          end
        }
        createdBy {
          id
        }
      }
    }
}
`;
